import type { AppIconName } from "@/components/common/app-icon";
import type { NodeAccent } from "../types/workflow";

import { AI_ASSISTANT } from "@/config/constants";

export type { NodeAccent };

export const NODE_ICONS: Record<string, AppIconName> = {
  contact_lifecycle_change: "solar:refresh-circle-bold",
  contact_created: "solar:user-plus-bold",
  order_created: "solar:cart-check-bold",
  appointment_created: "solar:calendar-add-bold",
  click_to_chat_ad: "solar:cursor-bold",
  order_status_update: "solar:box-bold",
  appointment_status_update: "solar:calendar-bold",
  appointment_outcome: "solar:clipboard-check-bold",
  comment_posted: "solar:chat-round-dots-bold",
  post_shared: "solar:share-circle-bold",
  story_reply: "solar:gallery-circle-bold",
  conversation_opened: "solar:chat-round-line-bold",
  conversation_closed: "solar:chat-round-check-bold",
  idle: "solar:clock-circle-bold",
  manual_trigger: "solar:play-circle-bold",
  tag_added: "solar:tag-bold",
  tag_removed: "solar:tag-horizontal-bold",
  if_else: "solar:transfer-horizontal-bold",
  router: "solar:routing-2-bold",
  create_follow_up: "solar:bell-bing-bold",
  wait_for_follow_up: "solar:alarm-pause-bold",
  delay_workflow: "solar:hourglass-bold",
  send_event_to_meta: "solar:bolt-bold",
  send_message: "solar:plain-bold",
  ask_question: "solar:chat-square-call-bold",
  send_comment: "solar:chat-round-dots-bold",
  change_contact_lifecycle: "solar:refresh-bold",
  update_appointment_status: "solar:calendar-mark-bold",
  formatter: "solar:magic-stick-3-bold",
  assign_conversation: "solar:user-check-bold",
  assign_to_ai: AI_ASSISTANT.icon,
  ai_agent: AI_ASSISTANT.icon,
  close_conversation: "solar:check-circle-bold",
  open_conversation: "solar:refresh-circle-bold",
  update_contact: "solar:user-bold",
  add_tag: "solar:tag-bold",
  remove_tag: "solar:tag-horizontal-bold",
  add_treatment: "solar:heart-pulse-bold",
  add_product: "solar:box-bold",
  add_note: "solar:notes-bold",
  start_workflow: "solar:play-circle-bold",
  go_to: "solar:reply-2-bold",
  exit: "solar:logout-3-bold",
  contact: "solar:user-rounded-bold",
};

// Node-family accent palette. One hue per family, expressed against theme
// tokens / mid-saturation hues so the icon + kind label stay legible on BOTH
// the light (design target) and dark surfaces.
export type AccentFamily =
  | "blue"
  | "violet"
  | "amber"
  | "cyan"
  | "green"
  | "gray";

export const ACCENT_FAMILY: Record<AccentFamily, NodeAccent> = {
  blue: {
    fg: "var(--accent)",
  },
  violet: {
    fg: "oklch(0.55 0.2 295)",
  },
  amber: {
    fg: "oklch(0.62 0.16 72)",
  },
  cyan: {
    fg: "oklch(0.55 0.11 220)",
  },
  green: {
    fg: "oklch(0.6 0.17 150.8)",
  },
  gray: {
    fg: "var(--muted)",
  },
};

// Each node type's accent family (matches the design's META.accent mapping).
export const NODE_FAMILY: Record<string, AccentFamily> = {
  contact_lifecycle_change: "violet",
  contact_created: "blue",
  order_created: "green",
  appointment_created: "green",
  click_to_chat_ad: "blue",
  order_status_update: "amber",
  appointment_status_update: "amber",
  appointment_outcome: "amber",
  comment_posted: "blue",
  post_shared: "blue",
  story_reply: "blue",
  conversation_opened: "blue",
  conversation_closed: "blue",
  idle: "amber",
  manual_trigger: "cyan",
  tag_added: "blue",
  tag_removed: "blue",
  if_else: "amber",
  router: "amber",
  create_follow_up: "green",
  wait_for_follow_up: "amber",
  delay_workflow: "amber",
  send_event_to_meta: "blue",
  send_message: "blue",
  ask_question: "blue",
  send_comment: "blue",
  change_contact_lifecycle: "violet",
  update_appointment_status: "amber",
  formatter: "amber",
  assign_conversation: "cyan",
  assign_to_ai: "violet",
  ai_agent: "violet",
  close_conversation: "cyan",
  open_conversation: "cyan",
  update_contact: "violet",
  add_tag: "violet",
  remove_tag: "violet",
  add_treatment: "violet",
  add_product: "violet",
  add_note: "violet",
  start_workflow: "amber",
  go_to: "amber",
  exit: "gray",
  contact: "blue",
};

export const FALLBACK_ACCENT: NodeAccent = ACCENT_FAMILY.blue;

// Per node-type accent, derived from the family map. Used for the icon chip
// on canvas cards, the config-panel header, and the picker tiles.
export const NODE_ACCENT: Record<string, NodeAccent> = Object.fromEntries(
  Object.entries(NODE_FAMILY).map(([key, family]) => [
    key,
    ACCENT_FAMILY[family],
  ]),
);

/** Resolve a node type's accent, falling back to its step-type family. */
export const getNodeAccent = (
  nodeType: string,
  stepType?: string,
): NodeAccent =>
  NODE_ACCENT[nodeType] ??
  (stepType ? STEP_TYPE_ACCENT[stepType] : undefined) ??
  FALLBACK_ACCENT;

// Per step-type accent - fallback family when a node type isn't mapped above.
export const STEP_TYPE_ACCENT: Record<string, NodeAccent> = {
  trigger: ACCENT_FAMILY.blue,
  action: ACCENT_FAMILY.violet,
  condition: ACCENT_FAMILY.amber,
  exit: ACCENT_FAMILY.gray,
};

// Friendly step-kind label for compact subtitles (e.g. the setup-issues popover).
export const STEP_TYPE_LABEL: Record<string, string> = {
  trigger: "Trigger",
  action: "Action",
  condition: "Condition",
  exit: "Exit",
};

// Branch outcome colors. Muted, desaturated tones (oklch ~0.7L / ~0.1C) so the
// then=green / else=red / invalid=amber semantic still reads at a glance without
// the neon glow of full-saturation hues on the canvas.
export const HANDLE_COLORS: Record<string, string> = {
  then: "#6db07f",
  else: "#ce7069",
  default: "#a1a1aa",
  no_match: "#a1a1aa",
  contact_replied: "#6db07f",
  timeout: "#ce7069",
  invalid: "#d1a662",
};

export const EDGE_COLORS: Record<string, string> = {
  ...HANDLE_COLORS,
  case: "#d1a662",
};

// Branch handles are functional anchors only - the edge stroke + label
// carry the visual, so keep the handle itself invisible.
export const BRANCH_HANDLE_BASE =
  "!w-1 !h-1 !opacity-0 !pointer-events-none !border-0 !min-w-0 !min-h-0";

export const TYPE_ICONS: Record<string, string> = {
  number: "solar:hashtag-linear",
  select: "solar:list-down-linear",
  lifecycle_select: "solar:list-down-linear",
  channel_select: "solar:list-down-linear",
  textarea: "solar:document-text-linear",
};

export const DEFAULT_TYPE_ICON = "solar:text-field-linear";
